import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { useForm } from 'react-hook-form';

import { ErrorMessage } from '@eika/attention-message';
import { StandardButton } from '@eika/button';
import { CurrencyInput } from '@eika/currency-input';
import { IconTypes } from '@eika/icon';
import { SearchInput } from '@eika/input';
import { Label } from '@eika/label';

import { FormTypeCalculatorVehicleInfo } from '../../../actions';
import { DispatchWithState, StringKeyValue } from '../../../actions/types';
import { CalculatorReducer } from '../../../reducers/calculator';
import { LoaderReducer } from '../../../reducers/loader';
import { VehicleReducer } from '../../../reducers/vehicle';
import { formatVehicleInfo } from '../../../util/common';
import { vehicleInfoSchema } from '../../../validation/loanCalculatorSchema';

interface Props {
    action: {
        fetchVehicleInfo: (form: FormTypeCalculatorVehicleInfo) => DispatchWithState;
    };
    calculator: CalculatorReducer;
    vehicle: VehicleReducer;
    loader: LoaderReducer;
    text: StringKeyValue;
}

const VehicleInfo = ({ action, calculator, vehicle, loader, text }: Props): JSX.Element => {
    const { fetchVehicleInfo } = action;
    const {
        loanInfo,
        isCar,
        isCarElectric,
        isCampingCar,
        isCampingCaravan,
        isObjectAgeEligible,
        isObjectZeroEmission,
    } = calculator;
    const { vehicleDetails, vehicleErrorNoMatch } = vehicle;
    const { odometerValue } = loanInfo;
    const { hasFetchedVehicleInfo } = loader;
    const vehicleInfo = formatVehicleInfo(
        vehicleDetails?.manufacturer,
        vehicleDetails?.model,
        vehicleDetails?.manufactureYear,
    );

    const {
        handleSubmit,
        register,
        setValue,
        formState: { errors },
    } = useForm<FormTypeCalculatorVehicleInfo>({
        resolver: yupResolver(vehicleInfoSchema),
        shouldUnregister: true,
        mode: 'all',
        defaultValues: {
            odometerValue: odometerValue || 0,
        },
    });

    const vehicleRegistrationNumberLength: number = isCar || isCampingCar ? 7 : 6;

    const lookupVehicle = (form: FormTypeCalculatorVehicleInfo) => {
        if (
            ((isCar || isCampingCar) && form.vehicleRegistrationNumber?.length === 7) ||
            (!isCar && form.vehicleRegistrationNumber?.length === 6)
        ) {
            fetchVehicleInfo(form);
        }
    };

    return (
        <div className="vehicle-info">
            <form onSubmit={handleSubmit(lookupVehicle)}>
                <div className="vehicle-info__reg-no input-wrapper">
                    <SearchInput
                        {...register('vehicleRegistrationNumber')}
                        label={text.calculatorVehicleInfoLabelRegNo}
                        minLength={vehicleRegistrationNumberLength}
                        error={errors?.vehicleRegistrationNumber?.message || ''}
                        onChange={(e: any) => {
                            setValue(
                                e.target.name,
                                e.target.value
                                    .replace(/[^\d\w]/g, '')
                                    .substring(0, vehicleRegistrationNumberLength)
                                    .toUpperCase(),
                            );
                        }}
                    />
                </div>

                {!isCampingCaravan && (
                    <div className="vehicle-info__odometer input-wrapper">
                        <CurrencyInput
                            {...register('odometerValue')}
                            label={text.calculatorVehicleInfoLabelOdometerValue}
                            placeholder={text.currencyInputPlaceholderKm}
                            error={String(errors?.odometerValue?.message || '')}
                            max={100000000000}
                            prefix=""
                            suffix=" km"
                            id="calculatorKm"
                        />
                    </div>
                )}

                {vehicleInfo && hasFetchedVehicleInfo && (
                    <div className="vehicle-info__result">
                        <Label text={vehicleInfo} />
                    </div>
                )}

                <div className="vehicle-info__button">
                    <StandardButton type="submit">{text.calculatorVehicleInfoButton}</StandardButton>
                </div>

                {!isObjectAgeEligible && (
                    <ErrorMessage
                        className="vehicle-info__error-message"
                        icon={IconTypes.LUKK_24}
                        text={text.calculatorVehicleInfoErrorObjectAge}
                    />
                )}

                {isCarElectric && !isObjectZeroEmission && (
                    <ErrorMessage
                        className="vehicle-info__error-message"
                        icon={IconTypes.LUKK_24}
                        text={text.calculatorVehicleInfoErrorZeroEmission}
                    />
                )}

                {vehicleErrorNoMatch && (
                    <ErrorMessage
                        className="vehicle-info__error-message"
                        icon={IconTypes.LUKK_24}
                        text={text.calculatorVehicleInfoErrorNoMatch}
                    />
                )}
            </form>
        </div>
    );
};

export default VehicleInfo;
